/**
 * ZoomableImage.jsx
 * Pinch-to-zoom + pan + double-tap + single-tap
 * Optimized: can be toggled on/off (zoomEnabled prop)
 */
import React from 'react';
import { View } from 'react-native';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withSpring,
    runOnJS,
} from 'react-native-reanimated';
import {
    Gesture,
    GestureDetector,
    GestureHandlerRootView,
} from 'react-native-gesture-handler';

const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;

const ZoomableImage = ({
    uri,
    width,
    height,
    style,
    resizeMode = 'cover',
    onPress,
    borderRadius = 0,
    zoomEnabled = true, // New prop to toggle zoom
}) => {
    const scale      = useSharedValue(1);
    const savedScale = useSharedValue(1);
    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);
    const savedX     = useSharedValue(0);
    const savedY     = useSharedValue(0);

    const resetTransform = (animated = true) => {
        'worklet';
        if (animated) {
            scale.value      = withSpring(1, { damping: 15 });
            translateX.value = withSpring(0, { damping: 15 });
            translateY.value = withSpring(0, { damping: 15 });
        } else {
            scale.value      = 1;
            translateX.value = 0;
            translateY.value = 0;
        }
        savedScale.value = 1;
        savedX.value     = 0;
        savedY.value     = 0;
    };

    // ── Taps ──────────────────────────────────────────────────────────────────
    const singleTap = Gesture.Tap()
        .numberOfTaps(1)
        .onEnd(() => {
            if (onPress) runOnJS(onPress)();
        });

    const doubleTap = Gesture.Tap()
        .numberOfTaps(2)
        .enabled(zoomEnabled)
        .onEnd((e) => {
            'worklet';
            if (scale.value > 1.1) {
                resetTransform();
            } else {
                scale.value = withSpring(DOUBLE_TAP_SCALE);
                savedScale.value = DOUBLE_TAP_SCALE;
                const offsetX = (width / 2 - e.x) * (DOUBLE_TAP_SCALE - 1) * 0.5;
                const offsetY = (height / 2 - e.y) * (DOUBLE_TAP_SCALE - 1) * 0.5;
                translateX.value = withSpring(offsetX);
                translateY.value = withSpring(offsetY);
                savedX.value = offsetX;
                savedY.value = offsetY;
            }
        });

    // ── Gestures ──────────────────────────────────────────────────────────────
    const pinchGesture = Gesture.Pinch()
        .enabled(zoomEnabled)
        .onUpdate((e) => {
            'worklet';
            scale.value = Math.min(Math.max(savedScale.value * e.scale, 0.8), MAX_SCALE);
        })
        .onEnd(() => {
            'worklet';
            if (scale.value < 1.1) {
                resetTransform();
            } else {
                savedScale.value = scale.value;
            }
        });

    const panGesture = Gesture.Pan()
        .enabled(zoomEnabled)
        .averageTouches(true)
        .onUpdate((e) => {
            'worklet';
            if (scale.value > 1.1) {
                translateX.value = savedX.value + e.translationX;
                translateY.value = savedY.value + e.translationY;
            }
        })
        .onEnd(() => {
            'worklet';
            if (scale.value > 1.1) {
                savedX.value = translateX.value;
                savedY.value = translateY.value;
            } else {
                resetTransform();
            }
        });

    const tapGesture = Gesture.Exclusive(doubleTap, singleTap);
    const composed = Gesture.Simultaneous(tapGesture, pinchGesture, panGesture);

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [
            { translateX: translateX.value },
            { translateY: translateY.value },
            { scale: scale.value },
        ],
    }));

    // If zoom is disabled, we don't need the complex composition or RootView
    // but we keep GestureDetector for the singleTap/onPress functionality.
    return (
        <GestureHandlerRootView style={[{ width, height, overflow: 'hidden', borderRadius }, style]}>
            <GestureDetector gesture={composed}>
                <Animated.View style={[{ width, height, backgroundColor: '#000000' }]}>
                    <Animated.Image
                        source={typeof uri === 'string' ? { uri } : uri}
                        style={[{ width, height }, animatedStyle]}
                        resizeMode={resizeMode}
                    />
                </Animated.View>
            </GestureDetector>
        </GestureHandlerRootView>
    );
};

export default React.memo(ZoomableImage);
